import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import type { OverviewCardDataProps } from '@ctw/shared/content/overview/resource-overview-card';
import { ResourceOverviewCard } from '@ctw/shared/content/overview/resource-overview-card';
import { PatientRiskGapsResourceTableRow } from '@ctw/shared/content/patient-risk-gaps/components/resource-table';
import { buildPatientRiskGapsColumnsV2 } from '@ctw/shared/content/patient-risk-gaps/helpers/columns-v2';
import { tw } from '@ctw/shared/utils/tailwind';
import { faStarExclamation } from '@fortawesome/pro-solid-svg-icons';
import { Badge } from '../../components/badge';
import { ResourceTitleColumn } from '../resource/resource-title-column';
import { riskGapStatusBadgeDefinition } from '../risk/risk-gap-status-badge-definition';

const RISK_GAP_OVERVIEW_COLUMNS = buildPatientRiskGapsColumnsV2();

export interface PatientRiskGapOverviewCardProps extends OverviewCardDataProps<RiskGapModel> {
  /** Switches between the workflow-preview row layout and the legacy drawer layout. */
  workflowPreviewEnabled: boolean;
}

/**
 * Presentational risk capture overview card. Renders the pre-filtered/sorted
 * list it is handed; data fetching lives in `usePatientRiskGapsOverviewData`.
 * The two render variants are selected by `workflowPreviewEnabled`; the matching
 * row-click behavior is supplied by the container via `onRowClick`.
 */
export const PatientRiskGapOverviewCard = ({
  workflowPreviewEnabled,
  data,
  isLoading,
  emptyState,
  footerCTA,
  onRowClick,
}: PatientRiskGapOverviewCardProps) => (
  <ResourceOverviewCard
    onRowClick={onRowClick}
    data={data}
    emptyState={emptyState}
    footerCTA={footerCTA}
    title="Risk Capture"
    helpText="Risk suspecting and recapture opportunities for the current calendar year"
    loading={isLoading}
    headerIcon={faStarExclamation}
    testId="risk-gaps-overview"
    rowClassName={workflowPreviewEnabled ? tw`p-0` : undefined}
    renderResource={
      workflowPreviewEnabled
        ? (record: RiskGapModel, index: number) => (
            <PatientRiskGapsResourceTableRow
              variant={'active'}
              title={'overview'}
              record={record}
              rowIndex={index}
              displayedRecords={data}
              // The ResourceOverviewCard provides its own background hover.
              hasBackgroundHover={false}
              shouldMinimize={true}
              columns={RISK_GAP_OVERVIEW_COLUMNS}
            />
          )
        : (record: RiskGapModel) => (
            <div className={tw`flex justify-between text-left`}>
              <div className={tw`w-full flex-grow text-sm`}>
                <ResourceTitleColumn
                  className={tw`w-full`}
                  titleClassName={tw`group-hover:no-underline`}
                  title={
                    <div className={tw`flex h-full w-full flex-col gap-[3px]`}>
                      <div className={tw`flex w-full items-start justify-between`}>
                        <div
                          className={tw`w-full font-medium text-content-main text-sm group-hover:underline`}
                        >
                          {record.title}
                        </div>
                        <Badge
                          className={tw`mx min-w-40 justify-end text-wrap`}
                          {...riskGapStatusBadgeDefinition(record)}
                        />
                      </div>
                      <div className={tw`font-normal text-content-subtle text-sm`}>
                        {record.lastDiagnosedDisplay && <>Last Dx {record.lastDiagnosedDisplay}</>}
                      </div>
                    </div>
                  }
                />
              </div>
            </div>
          )
    }
  />
);
